/**
 * Generated by orval 🍺
 * Do not edit manually.
 * DevSMS API v2
 * The dashboard's API (LLD §26.2), and the source Orval generates the
 * dashboard's client from (`dashboard/orval.config.ts`).
 *
 * This document describes the Go server as built. It is held to the code
 * by `test/contract`: the routes it lists are exactly the routes served,
 * in normal and in standalone mode (`TestV2Routes_MatchTheSpecInBothModes`),
 * and every answer the integration, contract and end-to-end tests provoke
 * is validated against the operation it documents
 * (`test/harness/apispec.Validating`). Spectral lints it with
 * `api/.spectral.yaml` (`make spec`).
 *
 * Conventions:
 *   - JSON is camelCase; enums are snake_case; times are RFC 3339 UTC.
 *   - Money is a whole number of XAF (ISO 4217 exponent 0) in a plain
 *     integer field, with a sibling `currency` field (always `XAF`) on
 *     the objects that carry one. There is no `{amount, currency}`
 *     wrapper object (DV-145).
 *   - Lists that can grow are keyset-paged: `?cursor=&limit=` in,
 *     `{items, nextCursor, hasMore}` out. A cursor is opaque and signed;
 *     a tampered one answers 422 `invalid_cursor`. Short, bounded lists
 *     answer `{items}`.
 *   - Errors are RFC 9457 `application/problem+json`. The machine code is
 *     the last segment of `type`, in kebab case
 *     (`…/problems/wallet-insufficient` is the code `wallet_insufficient`);
 *     `x-problem-codes` lists every code with its status. There is no
 *     separate `code` member (DV-145).
 *   - Authentication is a bearer access token from `POST /auth/login`
 *     (or Google, TOTP, refresh, invite). API keys are not accepted on
 *     v2; they are the v1 credential.
 *   - `x-permission` names the permission an operation requires (every
 *     one listed, when it is a list). Account routes (`/account/*`) also
 *     admit any staff user without it (DV-55). Every `/admin/*` route
 *     also refuses a staff user who has not enrolled TOTP (403
 *     `totp_enrolment_required`).
 *   - `x-step-up: true` marks an operation that needs an `X-Step-Up`
 *     token from `POST /auth/step-up`, under 5 minutes old (403
 *     `step_up_required` otherwise).
 *   - `x-idempotency: required` marks an operation that needs an
 *     `Idempotency-Key` header (422 `idempotency_key_required`); a
 *     repeat replays the first answer byte for byte with
 *     `Idempotent-Replay: true`. `x-idempotency: exempt` marks a write
 *     in a money area that deliberately takes none, and
 *     `x-idempotency-reason` says why.
 *   - `x-standalone: absent` marks an operation that is not mounted when
 *     `STANDALONE_MODE=true` (it answers 404 there).
 *   - `?lang=fr|en` (or `Accept-Language`) chooses the language of every
 *     problem's `detail` and of the PDFs; French is the default.
 *   - CORS admits one origin, the deployment's `DASHBOARD_URL`, with
 *     credentials (none when it is unset; never `*`). Preflights are
 *     answered `204` before authentication and rate limiting. Allowed
 *     request headers: `Authorization`, `Content-Type`,
 *     `Accept-Language`, `Idempotency-Key`, `X-Step-Up`, `X-Sdk-Client`;
 *     exposed: `Idempotent-Replay`, `RateLimit-Limit`,
 *     `RateLimit-Remaining`, `RateLimit-Reset`, `Retry-After`,
 *     `X-Request-Id`, `Content-Disposition`. (`/api/v1` keeps Nest's
 *     `enableCors()`: `Access-Control-Allow-Origin: *`, no credentials.)
 *
 * OpenAPI spec version: 2.0.0
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseInfiniteQueryResult,
  DefinedUseQueryResult,
  InfiniteData,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import type {
  ApiKey,
  ApiKeyPage,
  ConflictResponse,
  CreateApiKey201,
  CreateApiKeyBody,
  ForbiddenResponse,
  InternalErrorResponse,
  ListApiKeysParams,
  NotFoundResponse,
  PayloadTooLargeResponse,
  RenameApiKeyBody,
  UnauthorizedResponse,
  UnavailableResponse,
  UnprocessableResponse
} from '../model';

import { apiFetch } from '../../mutator.ts';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];



const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export const getListApiKeysUrl = (params?: ListApiKeysParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api-keys?${stringifiedParams}` : `/api-keys`
}

/**
 * @summary The caller's API keys, newest first
 */
export const listApiKeys = async (params?: ListApiKeysParams, options?: Parameters<typeof apiFetch>[1]): Promise<ApiKeyPage> => {

  return apiFetch<ApiKeyPage>(getListApiKeysUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListApiKeysInfiniteQueryKey = (params?: ListApiKeysParams,) => {
    return [
    'infinite', `/api-keys`, ...(params ? [params] : [])
    ] as const;
    }

export const getListApiKeysQueryKey = (params?: ListApiKeysParams,) => {
    return [
    `/api-keys`, ...(params ? [params] : [])
    ] as const;
    }


export const getListApiKeysInfiniteQueryOptions = <TData = InfiniteData<Awaited<ReturnType<typeof listApiKeys>>, ListApiKeysParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: ListApiKeysParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listApiKeys>>, TError, TData, QueryKey, ListApiKeysParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListApiKeysInfiniteQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listApiKeys>>, QueryKey, ListApiKeysParams['cursor']> = ({ signal, pageParam }) => listApiKeys({...params, ...((c) => c === undefined ? {} : { 'cursor': c })(pageParam ?? params?.['cursor'])}, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof listApiKeys>>, TError, TData, QueryKey, ListApiKeysParams['cursor']> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListApiKeysInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof listApiKeys>>>
export type ListApiKeysInfiniteQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useListApiKeysInfinite<TData = InfiniteData<Awaited<ReturnType<typeof listApiKeys>>, ListApiKeysParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  ListApiKeysParams, options: { query:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listApiKeys>>, TError, TData, QueryKey, ListApiKeysParams['cursor']>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listApiKeys>>,
          TError,
          Awaited<ReturnType<typeof listApiKeys>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListApiKeysInfinite<TData = InfiniteData<Awaited<ReturnType<typeof listApiKeys>>, ListApiKeysParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListApiKeysParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listApiKeys>>, TError, TData, QueryKey, ListApiKeysParams['cursor']>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listApiKeys>>,
          TError,
          Awaited<ReturnType<typeof listApiKeys>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListApiKeysInfinite<TData = InfiniteData<Awaited<ReturnType<typeof listApiKeys>>, ListApiKeysParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListApiKeysParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listApiKeys>>, TError, TData, QueryKey, ListApiKeysParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The caller's API keys, newest first
 */

export function useListApiKeysInfinite<TData = InfiniteData<Awaited<ReturnType<typeof listApiKeys>>, ListApiKeysParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListApiKeysParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listApiKeys>>, TError, TData, QueryKey, ListApiKeysParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListApiKeysInfiniteQueryOptions(params,options)

  const query = useInfiniteQuery(queryOptions, queryClient) as  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getListApiKeysQueryOptions = <TData = Awaited<ReturnType<typeof listApiKeys>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: ListApiKeysParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listApiKeys>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListApiKeysQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listApiKeys>>> = ({ signal }) => listApiKeys(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listApiKeys>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListApiKeysQueryResult = NonNullable<Awaited<ReturnType<typeof listApiKeys>>>
export type ListApiKeysQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useListApiKeys<TData = Awaited<ReturnType<typeof listApiKeys>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  ListApiKeysParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listApiKeys>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listApiKeys>>,
          TError,
          Awaited<ReturnType<typeof listApiKeys>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListApiKeys<TData = Awaited<ReturnType<typeof listApiKeys>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListApiKeysParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listApiKeys>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listApiKeys>>,
          TError,
          Awaited<ReturnType<typeof listApiKeys>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListApiKeys<TData = Awaited<ReturnType<typeof listApiKeys>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListApiKeysParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listApiKeys>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The caller's API keys, newest first
 */

export function useListApiKeys<TData = Awaited<ReturnType<typeof listApiKeys>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListApiKeysParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listApiKeys>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListApiKeysQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getCreateApiKeyUrl = () => {




  return `/api-keys`
}

/**
 * The raw key is in this answer only; the body is optional.
 * @summary Issue an API key
 */
export const createApiKey = async (createApiKeyBody?: CreateApiKeyBody, options?: Parameters<typeof apiFetch>[1]): Promise<CreateApiKey201> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<CreateApiKey201>(getCreateApiKeyUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createApiKeyBody)
  }
);}





export const getCreateApiKeyMutationKey = () => ['createApiKey'] as const;

export const getCreateApiKeyMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createApiKey>>, TError,CreateApiKeyMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createApiKey>>, TError,CreateApiKeyMutationVariables, TContext> => {

const mutationKey = getCreateApiKeyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createApiKey>>, CreateApiKeyMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createApiKey(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateApiKeyMutationResult = NonNullable<Awaited<ReturnType<typeof createApiKey>>>
    export type CreateApiKeyMutationBody = CreateApiKeyBody | undefined
    export type CreateApiKeyMutationError = UnauthorizedResponse | ForbiddenResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type CreateApiKeyMutationVariables = {data?: CreateApiKeyBody}

    /**
 * @summary Issue an API key
 */
export const useCreateApiKey = <TError = UnauthorizedResponse | ForbiddenResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createApiKey>>, TError,CreateApiKeyMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createApiKey>>,
        TError,
        CreateApiKeyMutationVariables,
        TContext
      > => {
      return useMutation(getCreateApiKeyMutationOptions(options), queryClient);
    }
    export const getRenameApiKeyUrl = (id: string,) => {




  return `/api-keys/${id}`
}

/**
 * @summary Rename a key
 */
export const renameApiKey = async (id: string,
    renameApiKeyBody: RenameApiKeyBody, options?: Parameters<typeof apiFetch>[1]): Promise<ApiKey> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<ApiKey>(getRenameApiKeyUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(renameApiKeyBody)
  }
);}





export const getRenameApiKeyMutationKey = () => ['renameApiKey'] as const;

export const getRenameApiKeyMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof renameApiKey>>, TError,RenameApiKeyMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof renameApiKey>>, TError,RenameApiKeyMutationVariables, TContext> => {

const mutationKey = getRenameApiKeyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof renameApiKey>>, RenameApiKeyMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  renameApiKey(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RenameApiKeyMutationResult = NonNullable<Awaited<ReturnType<typeof renameApiKey>>>
    export type RenameApiKeyMutationBody = RenameApiKeyBody
    export type RenameApiKeyMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type RenameApiKeyMutationVariables = {id: string;data: RenameApiKeyBody}

    /**
 * @summary Rename a key
 */
export const useRenameApiKey = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | PayloadTooLargeResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof renameApiKey>>, TError,RenameApiKeyMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof renameApiKey>>,
        TError,
        RenameApiKeyMutationVariables,
        TContext
      > => {
      return useMutation(getRenameApiKeyMutationOptions(options), queryClient);
    }
    export const getDeleteApiKeyUrl = (id: string,) => {




  return `/api-keys/${id}`
}

/**
 * A key must be revoked first (409 `not_revoked`).
 * @summary Delete a revoked key
 */
export const deleteApiKey = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

  return apiFetch<void>(getDeleteApiKeyUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteApiKeyMutationKey = () => ['deleteApiKey'] as const;

export const getDeleteApiKeyMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteApiKey>>, TError,DeleteApiKeyMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteApiKey>>, TError,DeleteApiKeyMutationVariables, TContext> => {

const mutationKey = getDeleteApiKeyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteApiKey>>, DeleteApiKeyMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteApiKey(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteApiKeyMutationResult = NonNullable<Awaited<ReturnType<typeof deleteApiKey>>>

    export type DeleteApiKeyMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type DeleteApiKeyMutationVariables = {id: string}

    /**
 * @summary Delete a revoked key
 */
export const useDeleteApiKey = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteApiKey>>, TError,DeleteApiKeyMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteApiKey>>,
        TError,
        DeleteApiKeyMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteApiKeyMutationOptions(options), queryClient);
    }
    export const getRevokeApiKeyUrl = (id: string,) => {




  return `/api-keys/${id}/revoke`
}

/**
 * @summary Revoke a key
 */
export const revokeApiKey = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<ApiKey> => {

  return apiFetch<ApiKey>(getRevokeApiKeyUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRevokeApiKeyMutationKey = () => ['revokeApiKey'] as const;

export const getRevokeApiKeyMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeApiKey>>, TError,RevokeApiKeyMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof revokeApiKey>>, TError,RevokeApiKeyMutationVariables, TContext> => {

const mutationKey = getRevokeApiKeyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokeApiKey>>, RevokeApiKeyMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  revokeApiKey(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RevokeApiKeyMutationResult = NonNullable<Awaited<ReturnType<typeof revokeApiKey>>>

    export type RevokeApiKeyMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type RevokeApiKeyMutationVariables = {id: string}

    /**
 * @summary Revoke a key
 */
export const useRevokeApiKey = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeApiKey>>, TError,RevokeApiKeyMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof revokeApiKey>>,
        TError,
        RevokeApiKeyMutationVariables,
        TContext
      > => {
      return useMutation(getRevokeApiKeyMutationOptions(options), queryClient);
    }
